// Gyroscope controls. When enabled, device orientation drives pitch + yaw,
// the same way the mouse cursor does on desktop. Roll/throttle/fire still
// come from the touch overlay.
//
// iOS Safari requires a user-gesture-triggered permission prompt
// (DeviceOrientationEvent.requestPermission). Other browsers just work once
// you start listening.

import type { InputSystem } from "./Input";

interface IOSDeviceOrientationCtor {
  requestPermission?: () => Promise<"granted" | "denied">;
}

export class GyroControls {
  enabled = false;
  private listening = false;
  // Calibration baseline. Re-set whenever the user toggles on or hits "calibrate".
  private baselineBeta = 0;
  private baselineGamma = 0;
  // Range over which tilt → ±1 input. ±28° is comfortable on a phone held in
  // landscape — the player isn't asked to tip the device past readable angles.
  private rangeDeg = 28;
  // Smoothing: gyro readings are noisy. Low-pass filter both axes.
  private smoothBeta = 0;
  private smoothGamma = 0;

  constructor(private input: InputSystem) {}

  static isAvailable(): boolean {
    return typeof window !== "undefined" && "DeviceOrientationEvent" in window;
  }

  // Must be called from a user-gesture event handler (toggle switch click).
  async enable(): Promise<boolean> {
    if (!GyroControls.isAvailable()) return false;
    const Ctor = (window as unknown as { DeviceOrientationEvent: IOSDeviceOrientationCtor }).DeviceOrientationEvent;
    if (Ctor && typeof Ctor.requestPermission === "function") {
      try {
        const res = await Ctor.requestPermission();
        if (res !== "granted") return false;
      } catch {
        return false;
      }
    }
    this.startListening();
    this.enabled = true;
    // Defer baseline so the first frames don't snap.
    setTimeout(() => this.calibrate(), 350);
    return true;
  }

  disable(): void {
    if (!this.listening) { this.enabled = false; return; }
    window.removeEventListener("deviceorientation", this.onOrient);
    this.listening = false;
    this.enabled = false;
    // Recenter the virtual cursor so the plane stops turning.
    this.input.mouseX = window.innerWidth  / 2;
    this.input.mouseY = window.innerHeight / 2;
  }

  calibrate(): void {
    this.baselineBeta = this.smoothBeta;
    this.baselineGamma = this.smoothGamma;
  }

  private startListening() {
    if (this.listening) return;
    this.listening = true;
    window.addEventListener("deviceorientation", this.onOrient, true);
  }

  private onOrient = (e: DeviceOrientationEvent) => {
    if (e.beta == null || e.gamma == null) return;
    // Low-pass smoothing — α ≈ 0.25 is a reasonable balance of lag vs noise.
    const a = 0.25;
    this.smoothBeta  = this.smoothBeta  * (1 - a) + e.beta  * a;
    this.smoothGamma = this.smoothGamma * (1 - a) + e.gamma * a;

    if (!this.enabled) return;
    // Map relative tilt to ±1 axes.
    const beta  = this.smoothBeta  - this.baselineBeta;   // pitch
    const gamma = this.smoothGamma - this.baselineGamma;  // yaw / roll
    const norm = (v: number) => Math.max(-1, Math.min(1, v / this.rangeDeg));
    // Tilt phone forward (beta increases when held in landscape and tipped
    // away) → nose-down dive. We invert beta so "tilt forward" = pitch DOWN
    // → cursor moves DOWN on screen → input.pitch negative.
    const py = norm(beta);
    const px = norm(gamma);

    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    const half = Math.min(cx, cy);
    this.input.mouseX = cx + px * half;
    this.input.mouseY = cy + py * half;
  };
}
